import { CartAddEvent, CartErrorEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

const UPSELL_SECTION_FILE = 'cart-upsell-products';

class UpsellAddError extends Error {
  /**
   * @param {string} message
   * @param {object} [details]
   */
  constructor(message, details = {}) {
    super(message);
    this.name = 'UpsellAddError';
    this.details = details;
  }
}

/**
 * Upsell da gaveta do carrinho.
 *
 * Busca sugestões do Search & Discovery via Section Rendering API
 * (rota de recomendações + section_id=cart-upsell-products) e injeta os cards.
 *
 * A gaveta inteira é re-renderizada (morph) a cada mudança no carrinho; o
 * atributo `data-cart-state` muda junto e o MutationObserver refaz a busca —
 * mesmo padrão do product-recommendations.js do tema.
 */
class CartUpsellComponent extends HTMLElement {
  #observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.type === 'attributes') {
        // Carrinho mudou de verdade: recarrega do servidor.
        this.#loadIfReady();
        return;
      }
    }
    // Só o DOM mudou (morph da gaveta sem mudança de carrinho — ex.: cupom
    // aplicado): o servidor sempre re-renderiza este elemento VAZIO e com
    // hidden, apagando os cards injetados. Restaura do cache local.
    if (this.#canLoad()) this.#restoreFromCache();
  });

  #dialogObserver = new MutationObserver(() => {
    if (this.#dialog?.open) this.#loadIfReady();
    else this.#activeFetch?.abort();
  });

  /** @type {AbortController | null} */
  #activeFetch = null;

  /** @type {{ state: string, html: string } | null} */
  #rendered = null;

  /** @type {HTMLDialogElement | null} */
  #dialog = null;

  #addInFlight = false;

  /** @type {HTMLElement | null} */
  #lockOwner = null;

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  #updateNav = () => {
    const scroller = this.querySelector('[data-upsell-scroller]');
    const nav = this.querySelector('.cart-upsell__nav');
    if (!(scroller instanceof HTMLElement) || !(nav instanceof HTMLElement)) return;

    const maxScroll = Math.max(0, scroller.scrollWidth - scroller.clientWidth);
    const hasOverflow = maxScroll > 1;
    nav.toggleAttribute('hidden', !hasOverflow);

    const prev = nav.querySelector('[data-upsell-prev]');
    const next = nav.querySelector('[data-upsell-next]');
    if (prev instanceof HTMLButtonElement) prev.disabled = !hasOverflow || scroller.scrollLeft <= 1;
    if (next instanceof HTMLButtonElement) next.disabled = !hasOverflow || scroller.scrollLeft >= maxScroll - 1;
  };

  /** Setas do carrossel — delegação: sobrevive ao replaceChildren do #load. */
  #onNavClick = (event) => {
    const retry = event.target.closest('[data-upsell-retry]');
    if (retry) {
      const form = retry.closest('product-form-component')?.querySelector('form[data-type="add-to-cart-form"]');
      if (form instanceof HTMLFormElement) {
        this.#hideAddError(form);
        form.requestSubmit();
      }
      return;
    }

    const prev = event.target.closest('[data-upsell-prev]');
    const next = event.target.closest('[data-upsell-next]');
    if (!prev && !next) return;
    const scroller = this.querySelector('[data-upsell-scroller]');
    if (!scroller) return;
    // 152px do card + 12px do gap. Animação própria via rAF: o smooth nativo
    // é cancelado pelo scroll-lock do dialog da gaveta.
    const delta = prev ? -164 : 164;
    const from = scroller.scrollLeft;
    const to = Math.max(0, Math.min(from + delta, scroller.scrollWidth - scroller.clientWidth));
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      scroller.scrollLeft = to;
      this.#updateNav();
      return;
    }
    const start = performance.now();
    const duration = 220;
    let done = false;
    const step = (now) => {
      const t = Math.min(1, (now - start) / duration);
      const ease = 1 - Math.pow(1 - t, 3);
      scroller.scrollLeft = from + (to - from) * ease;
      if (t < 1) requestAnimationFrame(step);
      else done = true;
    };
    requestAnimationFrame(step);
    // seguro: rAF pausado (aba oculta/throttle) -> salta direto
    setTimeout(() => {
      if (!done) scroller.scrollLeft = to;
    }, 250);
  };

  /**
   * A escolha de variante vive na propriedade DOM `selected`, que innerHTML
   * NÃO serializa. Sem isto, o #restoreFromCache (morph sem mudança de
   * carrinho — ex.: cupom aplicado) recriaria o select com a primeira
   * variante e "Adicionar" mandaria a variante errada.
   */
  #onVariantChange = (event) => {
    const select = event.target.closest('select[name="id"]');
    if (!select) return;
    for (const option of select.options) {
      if (option.selected) option.setAttribute('selected', '');
      else option.removeAttribute('selected');
    }

    const selected = select.selectedOptions[0];
    const card = select.closest('.cart-upsell__card');
    const priceNow = card?.querySelector('[data-upsell-price-now]');
    const priceCompare = card?.querySelector('[data-upsell-price-compare]');
    const priceBadge = card?.querySelector('[data-upsell-price-badge]');
    const image = card?.querySelector('.cart-upsell__media img');
    if (selected && priceNow instanceof HTMLElement) {
      priceNow.textContent = selected.dataset.upsellPriceNow || '';
    }
    if (selected && priceCompare instanceof HTMLElement) {
      priceCompare.textContent = selected.dataset.upsellPriceCompare || '';
      priceCompare.hidden = !selected.dataset.upsellPriceCompare;
    }
    if (selected && priceBadge instanceof HTMLElement) {
      priceBadge.textContent = selected.dataset.upsellPriceBadge || '';
      priceBadge.hidden = !selected.dataset.upsellPriceBadge;
    }
    if (selected && image instanceof HTMLImageElement && selected.dataset.upsellImageSrc) {
      image.src = selected.dataset.upsellImageSrc;
      image.srcset = selected.dataset.upsellImageSrcset || '';
      image.alt = selected.dataset.upsellImageAlt || '';
    }

    if (this.#rendered && this.#rendered.state === (this.dataset.cartState || '')) {
      this.#rendered.html = this.innerHTML;
    }
  };

  #onSubmit = (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches('form[data-type="add-to-cart-form"]')) return;

    event.preventDefault();
    const cartItems = this.closest('cart-items-component');
    const cartMutationBusy =
      typeof cartItems?.isMutationBusy === 'function'
        ? cartItems.isMutationBusy()
        : cartItems?.getAttribute('aria-busy') === 'true';
    if (
      !form.reportValidity() ||
      form.getAttribute('aria-busy') === 'true' ||
      this.#addInFlight ||
      cartMutationBusy
    ) return;

    this.#addInFlight = true;
    void this.#addProduct(form).finally(() => {
      this.#addInFlight = false;
    });
  };

  constructor() {
    super();
    // no constructor (e não no connectedCallback): o morph da gaveta pode
    // desconectar/reconectar o componente e a ordem dos callbacks desfaria
    // um listener adicionado/removido lá. Aqui roda uma única vez e morre
    // junto com o elemento.
    this.addEventListener('click', this.#onNavClick);
    this.addEventListener('change', this.#onVariantChange);
    this.addEventListener('submit', this.#onSubmit);
  }

  connectedCallback() {
    this.#observer.observe(this, {
      attributes: true,
      attributeFilter: ['data-cart-state'],
      childList: true,
    });
    this.#dialog = this.closest('dialog');
    if (this.#dialog) {
      this.#dialogObserver.observe(this.#dialog, { attributes: true, attributeFilter: ['open'] });
    }
    this.#loadIfReady();
  }

  #restoreFromCache() {
    if (!this.#rendered || this.children.length > 0) return;
    if (this.#rendered.state !== this.dataset.cartState) return;

    this.innerHTML = this.#rendered.html;
    this.hidden = false;
    this.#wireScroller();
  }

  disconnectedCallback() {
    this.#observer.disconnect();
    this.#dialogObserver.disconnect();
    this.#activeFetch?.abort();
    this.#resizeObserver?.disconnect();
    this.#dialog = null;
  }

  #canLoad() {
    return !this.#dialog || this.#dialog.open;
  }

  #loadIfReady() {
    if (!this.#canLoad()) return;

    const state = this.dataset.cartState || '';
    if (this.#rendered?.state === state) {
      this.#restoreFromCache();
      return;
    }

    void this.#load();
  }

  #wireScroller() {
    this.#resizeObserver?.disconnect();
    const scroller = this.querySelector('[data-upsell-scroller]');
    if (!(scroller instanceof HTMLElement)) return;

    scroller.addEventListener('scroll', this.#updateNav, { passive: true });
    this.#resizeObserver = new ResizeObserver(this.#updateNav);
    this.#resizeObserver.observe(scroller);
    requestAnimationFrame(this.#updateNav);
  }

  /**
   * @param {HTMLFormElement} form
   */
  async #addProduct(form) {
    const productForm = form.closest('product-form-component');
    const productId = productForm?.dataset.productId || '';
    const formData = new FormData(form);
    const variantId = formData.get('id')?.toString() || '';

    const sectionIds = new Set();
    for (const component of document.querySelectorAll('cart-items-component[data-section-id]')) {
      if (component instanceof HTMLElement && component.dataset.sectionId) {
        sectionIds.add(component.dataset.sectionId);
      }
    }
    if (sectionIds.size > 0) formData.set('sections', Array.from(sectionIds).join(','));
    formData.set('sections_url', window.location.pathname);

    this.#hideAddError(form);
    this.#setAddBusy(form, true);

    try {
      const config = fetchConfig('javascript', { body: formData });
      const response = await fetch(Theme.routes.cart_add_url, {
        ...config,
        headers: {
          ...config.headers,
          Accept: 'text/html',
        },
      });
      const data = await response.json();

      if (!response.ok || data.status) {
        const message =
          (typeof data.description === 'string' && data.description) ||
          (typeof data.message === 'string' && data.message) ||
          this.dataset.addErrorMessage || '';
        throw new UpsellAddError(message, data);
      }

      this.#announce(this.dataset.addedMessage || '');
      this.dispatchEvent(
        new CartAddEvent(undefined, form.id, {
          source: 'product-form-component',
          itemCount: Number(formData.get('quantity')) || 1,
          productId,
          variantId,
          sections: data.sections,
        })
      );
    } catch (error) {
      const message = error instanceof UpsellAddError ? error.message : this.dataset.addErrorMessage || '';
      const details = error instanceof UpsellAddError ? error.details : {};

      this.#showAddError(form, message);
      this.dispatchEvent(new CartErrorEvent(form.id, message, details.description, details.errors));
    } finally {
      this.#setAddBusy(form, false);
    }
  }

  /**
   * @param {HTMLFormElement} form
   * @param {boolean} busy
   */
  #setAddBusy(form, busy) {
    const button = form.querySelector('[data-upsell-add]');
    const label = form.querySelector('[data-upsell-add-label]');
    if (!(button instanceof HTMLButtonElement)) return;

    const cartItems = busy ? this.closest('cart-items-component') : this.#lockOwner || this.closest('cart-items-component');
    if (busy && cartItems instanceof HTMLElement) this.#lockOwner = cartItems;
    if (typeof cartItems?.setMutationBusy === 'function') {
      cartItems.setMutationBusy('upsell', busy);
    } else if (cartItems instanceof HTMLElement) {
      cartItems.toggleAttribute('data-upsell-busy', busy);
      const remainsBusy =
        cartItems.hasAttribute('data-upsell-busy') ||
        cartItems.hasAttribute('data-benefits-busy') ||
        cartItems.hasAttribute('data-cart-update-busy') ||
        cartItems.hasAttribute('data-discount-busy');
      cartItems.classList.toggle('cart-items-disabled', remainsBusy);
      if (remainsBusy) cartItems.setAttribute('aria-busy', 'true');
      else cartItems.removeAttribute('aria-busy');
    }
    if (!busy) this.#lockOwner = null;

    if (busy) {
      button.dataset.originalLabel = label?.textContent || '';
      button.disabled = true;
      form.setAttribute('aria-busy', 'true');
      if (label) label.textContent = this.dataset.addingLabel || label.textContent;
      return;
    }

    form.removeAttribute('aria-busy');
    if (label && button.dataset.originalLabel) label.textContent = button.dataset.originalLabel;
    delete button.dataset.originalLabel;
  }

  #announce(message) {
    if (!message) return;
    const liveRegion = this.closest('dialog')?.querySelector('[ref="liveRegion"]');
    if (!(liveRegion instanceof HTMLElement)) return;

    liveRegion.textContent = '';
    requestAnimationFrame(() => {
      if (liveRegion.isConnected) liveRegion.textContent = message;
    });
  }

  /**
   * @param {HTMLFormElement} form
   * @param {string} message
   */
  #showAddError(form, message) {
    const feedback = form.parentElement?.querySelector('[data-upsell-feedback]');
    const messageElement = feedback?.querySelector('[data-upsell-error-message]');
    if (!(feedback instanceof HTMLElement) || !(messageElement instanceof HTMLElement)) return;

    messageElement.textContent = message;
    feedback.hidden = false;
    feedback.querySelector('[data-upsell-retry]')?.focus();
  }

  /**
   * @param {HTMLFormElement} form
   */
  #hideAddError(form) {
    const feedback = form.parentElement?.querySelector('[data-upsell-feedback]');
    if (feedback instanceof HTMLElement) feedback.hidden = true;
  }

  async #load() {
    const ids = (this.dataset.productIds || '').split(',').filter(Boolean);
    if (!ids.length) {
      this.hidden = true;
      return;
    }

    this.#activeFetch?.abort();
    const controller = new AbortController();
    this.#activeFetch = controller;

    // A seleção e a ordem vêm do Search & Discovery. Se complementares estiver
    // vazio, a seção permanece oculta — não substituímos por relacionados.
    const intent = this.dataset.intent === 'related' ? 'related' : 'complementary';

    try {
      const candidates = await Promise.all(ids.slice(0, 3).map(async (productId) => {
        const url = `${this.dataset.url}?section_id=${UPSELL_SECTION_FILE}&product_id=${productId}&limit=10&intent=${intent}`;
        const response = await fetch(url, { signal: controller.signal });
        if (!response.ok) return null;

        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const candidate = doc.querySelector('[data-cart-upsell-content]');
        return candidate && Number(candidate.dataset.count) > 0 ? candidate : null;
      }));
      const content = candidates.find(Boolean) || null;

      if (content && Number(content.dataset.count) > 0) {
        this.replaceChildren(...content.children);
        this.hidden = false;
        this.#rendered = { state: this.dataset.cartState || '', html: this.innerHTML };
        this.#wireScroller();
        return;
      }

      this.replaceChildren();
      this.hidden = true;
      this.#rendered = null;
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') return;
      console.error('Cart upsell:', error);
      this.hidden = true;
    } finally {
      if (this.#activeFetch === controller) this.#activeFetch = null;
    }
  }
}

if (!customElements.get('cart-upsell-component')) {
  customElements.define('cart-upsell-component', CartUpsellComponent);
}
